import { useEffect, useMemo, useRef, useState } from "react";
import "./GeneratorPanel.css";
import {ModelViewer} from "./ModelViewer";

type Props = {
    phaseLabel: string;
    phaseTitle: string;
    imgUrl: string;

    onBack?: () => void;
    backDisabled?: boolean;

    // ✅ nuova prop: chiama backend e ritorna URL .obj
    onGenerate3D: (imgUrl: string) => Promise<string>;
};

export const GeneratorPanel = ({
                                   phaseLabel,
                                   phaseTitle,
                                   imgUrl,
                                   onBack,
                                   backDisabled,
                                   onGenerate3D,
                               }: Props) => {
    const pollIntervalMs = 20_000;
    const maxWaitMs = 5 * 60_000;
    const generationRunRef = useRef(0);
    const objectUrlRef = useRef<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [hasGenerationStarted, setHasGenerationStarted] = useState(false);
    const [modelUrl, setModelUrl] = useState<string | null>(null);
    const [remoteModelUrl, setRemoteModelUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [waitingText, setWaitingText] = useState<string | null>(null);

    const canGenerate = useMemo(() => !!imgUrl && !isGenerating, [imgUrl, isGenerating]);

    useEffect(() => {
        return () => {
            generationRunRef.current += 1;
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };
    }, []);

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const tryDownloadModel = async (url: string): Promise<string | null> => {
        try {
            const response = await fetch(url, { method: "GET", cache: "no-store" });
            if (!response.ok) return null;
            const blob = await response.blob();
            if (!blob || blob.size === 0) return null;
            return URL.createObjectURL(blob);
        } catch {
            return null;
        }
    };

    const waitUntilModelDownloaded = async (url: string, runId: number): Promise<string | null> => {
        const startedAt = Date.now();
        const totalAttempts = Math.ceil(maxWaitMs / pollIntervalMs);
        let seenLikelyCorsFailure = false;
        while (Date.now() - startedAt <= maxWaitMs) {
            if (generationRunRef.current !== runId) return null;
            const attempt = Math.floor((Date.now() - startedAt) / pollIntervalMs) + 1;
            const elapsedSec = Math.floor((Date.now() - startedAt) / 1000);
            setWaitingText(
                `Attendi senza ricaricare il browser: il processo impiega circa 70-80 secondi. ` +
                `Tentativo ${attempt}/${totalAttempts} (${elapsedSec}s trascorsi).`
            );
            const downloadedUrl = await tryDownloadModel(url);
            if (downloadedUrl) return downloadedUrl;
            if (!seenLikelyCorsFailure) {
                try {
                    const targetOrigin = new URL(url).origin;
                    if (targetOrigin !== window.location.origin) {
                        seenLikelyCorsFailure = true;
                        setWaitingText(
                            "Il file risulta non è ancora disponibile. " +
                            "Resto in attesa e riprovo automaticamente."
                        );
                    }
                } catch {
                    // ignore URL parse errors
                }
            }
            await sleep(pollIntervalMs);
        }
        return null;
    };

    const handleGenerate = async () => {
        if (!canGenerate) return;

        setError(null);
        setIsGenerating(true);
        setHasGenerationStarted(true);
        setModelUrl(null);
        setRemoteModelUrl(null);
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        setWaitingText("Attendi senza ricaricare il browser: il processo impiega circa 70-80 secondi. Provo a scaricare il file ogni 20 secondi.");
        generationRunRef.current += 1;
        const currentRunId = generationRunRef.current;

        try {
            const url = await onGenerate3D(imgUrl);
            if (generationRunRef.current !== currentRunId) return;

            const downloadedModelUrl = await waitUntilModelDownloaded(url, currentRunId);
            if (generationRunRef.current !== currentRunId) return;

            if (!downloadedModelUrl) {
                setError("Il modello non è ancora disponibile dopo 5 minuti. Riprova tra poco.");
                return;
            }

            objectUrlRef.current = downloadedModelUrl;
            setRemoteModelUrl(url);
            setModelUrl(downloadedModelUrl);
        } catch (e: any) {
            setError(e?.message ?? "Errore durante la generazione 3D");
        } finally {
            if (generationRunRef.current === currentRunId) {
                setIsGenerating(false);
                setWaitingText(null);
            }
        }
    };

    const openFullscreenViewer = () => {
        if (!remoteModelUrl) return;
        const demoUrl = new URL("/demo-3d", window.location.origin);
        demoUrl.searchParams.set("modelUrl", remoteModelUrl);
        window.open(demoUrl.toString(), "_blank", "noopener,noreferrer");
    };

    return (
        <main className="chatArea glassCard">
            <div className="chatHeader">
                <div className="chatHeaderLeft">
                    <span className="chatPhase">{phaseLabel}</span>
                    <span className="chatTitle">{phaseTitle}</span>
                </div>

                <div className="chatHeaderRight">
                    <button
                        className="chatBackBtn"
                        onClick={onBack}
                        disabled={backDisabled}
                        aria-label="Torna alla fase precedente"
                        type="button"
                    >
                        ← Torna alla fase precedente
                    </button>
                </div>
            </div>

            <div className="genLayout">
                <div className="imgArea">
                    <div className="viewerHeader">
                        <div className="viewerTitle">Rendering 2D</div>
                        <div className="viewerSub">Immagine di partenza</div>
                    </div>
                    {/* COLONNA SX: immagine 2D (si rimpicciolisce mentre genera) */}
                    <div className={`imgBody ${isGenerating ? "isGenerating" : ""}`}>
                        <img src={imgUrl} alt="Immagine di partenza" className="image2d" />
                        {isGenerating && (
                            <div className="imgBadge">
                                Sto preparando il 3D…
                            </div>
                        )}
                    </div>

                </div>


                {/* COLONNA DX: preview area 3D (visibile subito) */}
                <div className="viewerArea">
                    <div className="viewerHeader">
                        <div className="viewerTitle">Anteprima 3D</div>
                        <div className="viewerSub">Qui comparirà il modello appena pronto.</div>
                    </div>

                    <div className={`viewerBox ${isGenerating ? "loading" : ""}`}>
                        {/* placeholder / loader */}
                        {!modelUrl && (
                            <div className="viewerPlaceholder">
                                {isGenerating && <div className="spinner" /> }
                                <div className="loaderText">
                                    {isGenerating ? waitingText : "Nessun modello ancora. Premi “Genera 3D”."}
                                </div>
                            </div>
                        )}

                        {/* viewer vero e proprio */}
                        {modelUrl && (
                            <ModelViewer
                                url={modelUrl}
                                onLoadError={(msg) => {
                                    setError(msg);
                                    setModelUrl(null);
                                }}
                            />
                        )}
                    </div>

                    {error && <div className="genError">{error}</div>}

                    <div className="generateCtaContainer">
                        {remoteModelUrl && (
                            <button className="fullScreenBtn" onClick={openFullscreenViewer} type="button">
                                Apri visualizzazione 3D full screen
                            </button>
                        )}
                        {!hasGenerationStarted && (
                            <button className="generateCta" onClick={handleGenerate} disabled={!canGenerate}>
                                Genera 3D
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
};
